import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  TrendingUp, 
  TrendingDown,
  RefreshCw,
  BarChart3,
  Gem,
  Crown,
  Bitcoin,
  Home,
  Menu,
  LineChart,
  Globe,
  ArrowLeft,
  DollarSign,
  Filter,
  Grid3X3,
  List,
  Search,
  Star,
  Clock,
  ArrowUp,
  ArrowDown,
  Banknote,
  Zap,
  X,
  Plus,
  Loader2,
  UserPlus,
  AlertTriangle,
  Activity,
  Maximize2,
  Minimize2,
  ExternalLink,
  Shield,
  ChevronRight,
  Cpu,
  Building2,
  Heart,
  ShoppingCart,
  Fuel,
  Factory,
  Coins,
  Landmark,
  Plane,
  Pill,
  Tv,
  Coffee
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("token");
const LOGO_URL = "/doslions-logo.png";
const DOSLIONS_LOGO = "/doslions-logo.webp";

// Price Card Skeleton - Shows instantly while data loads
const PriceCardSkeleton = () => (
  <div className="bg-[#111111] border border-zinc-800 rounded-lg p-3 animate-pulse">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-zinc-800 rounded"></div>
        <div>
          <div className="w-12 h-4 bg-zinc-800 rounded mb-1"></div>
          <div className="w-20 h-3 bg-zinc-800/50 rounded"></div>
        </div>
      </div>
      <div className="w-14 h-6 bg-zinc-800 rounded"></div>
    </div>
    <div className="flex items-center justify-between">
      <div className="w-16 h-5 bg-zinc-800 rounded"></div>
      <div className="w-12 h-4 bg-zinc-800/50 rounded"></div>
    </div>
  </div>
);

// Section Skeleton - Shows category with placeholder cards
const SectionSkeleton = ({ title, count = 8 }) => (
  <div className="mb-8">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-6 h-6 bg-zinc-800 rounded animate-pulse"></div>
      <div className="w-32 h-5 bg-zinc-800 rounded animate-pulse"></div>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
      {Array(count).fill(0).map((_, i) => <PriceCardSkeleton key={i} />)}
    </div>
  </div>
);

// Sector definitions with icons and colors
const SECTOR_CONFIG = {
  Technology: { icon: Cpu, color: "blue", label: "Technology" },
  Finance: { icon: Landmark, color: "emerald", label: "Finance & Banking" },
  Healthcare: { icon: Heart, color: "pink", label: "Healthcare" },
  Consumer: { icon: ShoppingCart, color: "purple", label: "Consumer Goods" },
  Retail: { icon: ShoppingCart, color: "orange", label: "Retail" },
  Energy: { icon: Fuel, color: "amber", label: "Energy" },
  Industrial: { icon: Factory, color: "slate", label: "Industrial" },
  Aerospace: { icon: Plane, color: "sky", label: "Aerospace & Defense" },
  Biotech: { icon: Pill, color: "teal", label: "Biotech & Pharma" },
  Entertainment: { icon: Tv, color: "rose", label: "Entertainment" },
  Semiconductor: { icon: Cpu, color: "indigo", label: "Semiconductors" },
  REIT: { icon: Building2, color: "green", label: "Real Estate" },
  Crypto: { icon: Bitcoin, color: "yellow", label: "Cryptocurrency" },
  Commodities: { icon: Gem, color: "amber", label: "Commodities" },
};

// Sector Modal Component
const SectorModal = ({ isOpen, onClose, sector, items, onItemClick, miniCharts }) => {
  if (!isOpen) return null;

  const config = SECTOR_CONFIG[sector] || { icon: BarChart3, color: "zinc", label: sector };
  const IconComponent = config.icon;

  const isPositive = (change) => {
    if (!change) return true;
    const str = change.toString();
    return !str.startsWith('-') && str !== '0.00%';
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="sector-modal-overlay">
      <div 
        className="bg-[#0a0a0a] border border-zinc-800 rounded-xl shadow-2xl max-w-5xl w-full max-h-[85vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="sector-modal-content"
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b border-zinc-800 bg-gradient-to-r from-${config.color}-500/10 to-transparent`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg bg-${config.color}-500/20 flex items-center justify-center`}>
              <IconComponent className={`w-5 h-5 text-${config.color}-500`} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{config.label}</h3>
              <p className="text-xs text-zinc-500">{items.length} instruments available</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="sector-modal-close">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Items Grid */}
        <div className="p-4 overflow-y-auto max-h-[calc(85vh-120px)]">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <IconComponent className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">Loading sector data...</p>
              <p className="text-zinc-600 text-xs mt-1">Please wait while prices load</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {items.map((item, idx) => {
                const isUp = isPositive(item.change_percent);
                const sparkData = miniCharts[item.symbol] || [];
                
                return (
                  <div
                    key={idx}
                    className={`bg-zinc-900/50 border border-zinc-800 p-3 rounded-lg cursor-pointer hover:bg-zinc-800/50 hover:border-${config.color}-500/50 transition-all group`}
                    onClick={() => onItemClick(item.symbol, item.name, item.price)}
                    data-testid={`sector-item-${item.symbol}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-sm font-bold text-${config.color}-500`}>{item.symbol}</span>
                      {isUp ? <ArrowUp className="w-3 h-3 text-emerald-500" /> : <ArrowDown className="w-3 h-3 text-red-500" />}
                    </div>
                    <p className="text-xs text-zinc-500 truncate mb-2">{item.name}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-white">${item.price}</span>
                      {sparkData.length > 1 && (
                        <svg width="40" height="16" className="overflow-visible">
                          <path
                            d={sparkData.map((val, i) => {
                              const min = Math.min(...sparkData);
                              const max = Math.max(...sparkData);
                              const range = max - min || 1;
                              const x = (i / (sparkData.length - 1)) * 40;
                              const y = 16 - ((val - min) / range) * 14;
                              return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
                            }).join(' ')}
                            fill="none"
                            stroke={isUp ? '#22c55e' : '#ef4444'}
                            strokeWidth="1.5"
                          />
                        </svg>
                      )}
                    </div>
                    <p className={`text-xs font-bold mt-1 ${isUp ? 'text-emerald-500' : 'text-red-500'}`}>
                      {item.change_percent}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Footer Disclaimer Component
const FooterDisclaimer = ({ onUpgrade }) => (
  <div className="mt-8 border-t border-zinc-800 pt-6 pb-8">
    <div className="max-w-4xl mx-auto text-center">
      {/* DOSLIONS Logo */}
      <div className="flex items-center justify-center gap-3 mb-4">
        <span className="text-xs text-zinc-600 uppercase tracking-wider">Powered by</span>
        <a href="https://doslions.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
          <img src={DOSLIONS_LOGO} alt="DOSLIONS" className="h-12 w-auto" />
        </a>
      </div>
      
      {/* Upgrade Button */}
      <div className="mb-5">
        <Button
          onClick={onUpgrade}
          className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold px-6 py-2 text-sm"
        >
          <Crown className="w-4 h-4 mr-2" />
          Become Pro Ecopreneur
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
        <p className="text-xs text-zinc-600 mt-2">$49.97/month • Unlock AI insights, detailed charts & more</p>
      </div>
      
      {/* Legal Disclaimer */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 max-w-2xl mx-auto">
        <div className="flex items-start gap-2 text-left">
          <Shield className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              <strong className="text-amber-500">DISCLAIMER:</strong> The information provided on this platform is for 
              <strong className="text-white"> educational and informational purposes only</strong>. 
              It does <strong className="text-white">NOT</strong> constitute financial, investment, legal, or tax advice. 
              Past performance is not indicative of future results. Trading and investing in financial markets involves 
              substantial risk of loss. Always consult with a qualified financial advisor before making investment decisions.
            </p>
            <p className="text-[10px] text-zinc-600 mt-2">
              © {new Date().getFullYear()} ECOLIONS AI Markets • Data provided by yFinance • Not affiliated with any exchange
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Mini Sparkline Chart Component - Synchronized with price
const MiniSparkline = ({ data, width = 60, height = 24, positive = true }) => {
  if (!data || data.length < 2) {
    return (
      <div className="animate-pulse bg-zinc-800/50 rounded" style={{ width, height }}></div>
    );
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  // Generate SVG path
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  });

  const pathD = `M ${points.join(' L ')}`;
  const color = positive ? '#22c55e' : '#ef4444';

  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={`spark-grad-${positive ? 'up' : 'down'}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Fill area */}
      <path
        d={`${pathD} L ${width},${height} L 0,${height} Z`}
        fill={`url(#spark-grad-${positive ? 'up' : 'down'})`}
      />
      {/* Line */}
      <path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Live dot */}
      <circle
        cx={width}
        cy={height - ((data[data.length - 1] - min) / range) * (height - 4) - 2}
        r="2"
        fill={color}
      >
        <animate attributeName="r" values="1.5;3;1.5" dur="2s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
};

// Chart Popup Modal Component - TradingView Powered for Fast Live Data
const ChartPopup = ({ symbol, name, currentPrice, isOpen, onClose }) => {
  const [timeframe, setTimeframe] = useState('D');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [priceData, setPriceData] = useState(null);

  const timeframes = [
    { label: '1D', value: 'D' },
    { label: '1W', value: 'W' },
    { label: '1M', value: 'M' },
    { label: '1Y', value: '12M' },
    { label: 'ALL', value: '60M' }
  ];

  // Get TradingView compatible symbol
  const getTradingViewSymbol = useCallback(() => {
    const upperSymbol = symbol.toUpperCase();
    // Normalize forex symbols: "EUR/USD" → "EURUSD"
    const normalized = upperSymbol.replace('/', '');
    
    // Index mapping
    const indexMap = {
      'SPX': 'SP:SPX', 'GSPC': 'SP:SPX', '^GSPC': 'SP:SPX',
      'NDX': 'NASDAQ:NDX', 'IXIC': 'NASDAQ:IXIC', '^IXIC': 'NASDAQ:IXIC', 'NASDAQ': 'NASDAQ:IXIC',
      'DJI': 'DJ:DJI', '^DJI': 'DJ:DJI', 'DOW': 'DJ:DJI',
      'RUT': 'TVC:RUT', '^RUT': 'TVC:RUT',
      'VIX': 'TVC:VIX', '^VIX': 'TVC:VIX',
      'TNX': 'TVC:TNX', '^TNX': 'TVC:TNX',
      'FTSE': 'TVC:UKX', '^FTSE': 'TVC:UKX',
      'N225': 'TVC:NI225', '^N225': 'TVC:NI225'
    };
    
    // Commodity mapping — use TVC spot/CFD symbols (universally embeddable)
    const commodityMap = {
      'GC': 'TVC:GOLD', 'GOLD': 'TVC:GOLD',
      'SI': 'TVC:SILVER', 'SILVER': 'TVC:SILVER',
      'CL': 'TVC:USOIL', 'OIL': 'TVC:USOIL', 'CRUDE': 'TVC:USOIL',
      'BZ': 'TVC:UKOIL',
      'NG': 'PEPPERSTONE:NATGAS', 'NATGAS': 'PEPPERSTONE:NATGAS',
      'RB': 'NYMEX:RB1!',
      'HO': 'NYMEX:HO1!',
      'HG': 'TVC:COPPER', 'COPPER': 'TVC:COPPER',
      'ALI': 'COMEX:ALI1!',
      'PL': 'TVC:PLATINUM', 'PLATINUM': 'TVC:PLATINUM',
      'PA': 'TVC:PALLADIUM', 'PALLADIUM': 'TVC:PALLADIUM',
      // Grains — use ETF equivalents for embed compatibility
      'ZW': 'AMEX:WEAT', 'ZC': 'AMEX:CORN', 'ZS': 'AMEX:SOYB',
      'ZR': 'CBOT:ZR1!', 'ZO': 'CBOT:ZO1!',
      // Softs
      'KC': 'ICEUS:KC1!', 'CT': 'ICEUS:CT1!', 'SB': 'ICEUS:SB1!',
      'CC': 'ICEUS:CC1!', 'OJ': 'ICEUS:OJ1!',
      // Livestock
      'LE': 'CME:LE1!', 'HE': 'CME:HE1!', 'GF': 'CME:GF1!',
      // Other
      'LBS': 'CME:LBS1!',
      'DX-Y.NYB': 'TVC:DXY', 'DXY': 'TVC:DXY'
    };
    
    // International stock exchange mapping
    const internationalMap = {
      'NESN.SW': 'SIX:NESN',
      'MC.PA': 'EURONEXT:MC',
    };
    
    // Crypto symbols (comprehensive)
    const cryptoMap = {
      'BTC': 'COINBASE:BTCUSD', 'ETH': 'COINBASE:ETHUSD',
      'XRP': 'BINANCE:XRPUSDT', 'BNB': 'BINANCE:BNBUSDT',
      'SOL': 'COINBASE:SOLUSD', 'ADA': 'COINBASE:ADAUSD',
      'DOGE': 'COINBASE:DOGEUSD', 'DOT': 'COINBASE:DOTUSD',
      'MATIC': 'COINBASE:MATICUSD', 'AVAX': 'COINBASE:AVAXUSD',
      'LINK': 'COINBASE:LINKUSD', 'SHIB': 'COINBASE:SHIBUSD',
      'LTC': 'COINBASE:LTCUSD', 'TRX': 'BINANCE:TRXUSDT',
      'USDT': 'BINANCE:USDCUSDT',
      'ATOM': 'COINBASE:ATOMUSD', 'UNI': 'COINBASE:UNIUSD',
      'XLM': 'COINBASE:XLMUSD', 'NEAR': 'COINBASE:NEARUSD',
      'FTM': 'BINANCE:FTMUSDT', 'ALGO': 'COINBASE:ALGOUSD',
      'VET': 'BINANCE:VETUSDT', 'HBAR': 'BINANCE:HBARUSDT',
      'ICP': 'COINBASE:ICPUSD', 'FIL': 'COINBASE:FILUSD',
      'APT': 'COINBASE:APTUSD', 'ARB': 'COINBASE:ARBUSD',
    };
    
    // Forex symbols (match both "EURUSD" and "EUR/USD")
    const forexMap = {
      'EURUSD': 'FX:EURUSD', 'GBPUSD': 'FX:GBPUSD', 'USDJPY': 'FX:USDJPY',
      'AUDUSD': 'FX:AUDUSD', 'USDCAD': 'FX:USDCAD', 'USDCHF': 'FX:USDCHF',
      'NZDUSD': 'FX:NZDUSD',
    };
    
    if (indexMap[upperSymbol]) return indexMap[upperSymbol];
    if (commodityMap[upperSymbol]) return commodityMap[upperSymbol];
    if (internationalMap[upperSymbol]) return internationalMap[upperSymbol];
    if (cryptoMap[upperSymbol]) return cryptoMap[upperSymbol];
    if (forexMap[normalized]) return forexMap[normalized];
    
    // Default to stock (works for most US/international stocks on NASDAQ/NYSE)
    return upperSymbol;
  }, [symbol]);

  // Fetch price data for info panel
  useEffect(() => {
    if (isOpen && symbol) {
      const fetchData = async () => {
        try {
          const res = await axios.get(`${API}/price-report/${symbol}`);
          if (res.data?.success) {
            setPriceData(res.data);
          }
        } catch (e) {
          setPriceData({
            current_price: currentPrice,
            change_1d: 0,
            sentiment: 'Neutral'
          });
        }
      };
      fetchData();
    }
  }, [isOpen, symbol, currentPrice]);

  if (!isOpen) return null;

  const tvSymbol = getTradingViewSymbol();

  // Get sentiment
  const getSentiment = () => {
    const change = priceData?.change_1d || 0;
    if (change > 3) return { text: 'Very Bullish', color: 'emerald', bgColor: 'bg-emerald-500/20', borderColor: 'border-emerald-500/50' };
    if (change > 0) return { text: 'Bullish', color: 'green', bgColor: 'bg-green-500/20', borderColor: 'border-green-500/50' };
    if (change > -3) return { text: 'Bearish', color: 'red', bgColor: 'bg-red-500/20', borderColor: 'border-red-500/50' };
    return { text: 'Very Bearish', color: 'red', bgColor: 'bg-red-500/20', borderColor: 'border-red-500/50' };
  };
  const sentiment = getSentiment();

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${isFullscreen ? '' : 'p-4'}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className={`relative bg-[#0a0a0a] border border-zinc-800 shadow-2xl ${
        isFullscreen 
          ? 'w-full h-full' 
          : 'w-full max-w-4xl max-h-[90vh] rounded-xl overflow-hidden'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800 bg-zinc-900/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 flex items-center justify-center rounded-lg">
              <BarChart3 className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{symbol}</h3>
              <p className="text-sm text-zinc-400">{name}</p>
            </div>
            <span className="ml-2 px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded animate-pulse">LIVE</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="text-zinc-400 hover:text-white"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Timeframe selector */}
        <div className="flex items-center gap-2 p-3 border-b border-zinc-800 bg-zinc-900/30">
          {timeframes.map(tf => (
            <button
              key={tf.value}
              onClick={() => setTimeframe(tf.value)}
              className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${
                timeframe === tf.value 
                  ? 'bg-emerald-500 text-black' 
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
              }`}
            >
              {tf.label}
            </button>
          ))}
          <div className="ml-auto text-xs text-zinc-500">
            Powered by TradingView • Real-time data
          </div>
        </div>

        {/* Chart area with TradingView */}
        <div className={`${isFullscreen ? 'flex h-[calc(100%-130px)]' : 'h-[450px]'}`}>
          {/* TradingView Chart */}
          <div className={`${isFullscreen ? 'flex-1' : 'w-full h-full'} bg-black`}>
            <iframe
              key={`${tvSymbol}-${timeframe}`}
              src={`https://s.tradingview.com/widgetembed/?frameElementId=tradingview_popup&symbol=${tvSymbol}&interval=${timeframe}&hidesidetoolbar=0&symboledit=0&saveimage=1&toolbarbg=111111&studies=RSI%40tv-basicstudies&theme=dark&style=1&timezone=exchange&withdateranges=1&showpopupbutton=0&studies_overrides=%7B%7D&overrides=%7B%7D&enabled_features=%5B%5D&disabled_features=%5B%22header_symbol_search%22%5D&locale=en`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={`TradingView Chart - ${symbol}`}
              allowFullScreen
            />
          </div>
          
          {/* Info Panel - Only in Fullscreen */}
          {isFullscreen && (
            <div className="w-80 border-l border-zinc-800 bg-zinc-900/50 p-4 space-y-4 overflow-y-auto">
              {/* Price Summary */}
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-500" />
                  Price Summary
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-zinc-900 p-2 rounded">
                    <p className="text-[10px] text-zinc-500 uppercase">Current</p>
                    <p className="text-lg font-bold text-white">{currentPrice}</p>
                  </div>
                  <div className="bg-zinc-900 p-2 rounded">
                    <p className="text-[10px] text-zinc-500 uppercase">Change 1D</p>
                    <p className={`text-lg font-bold ${(priceData?.change_1d || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {(priceData?.change_1d || 0) >= 0 ? '+' : ''}{(priceData?.change_1d || 0).toFixed(2)}%
                    </p>
                  </div>
                  {priceData?.high_24h && (
                    <div className="bg-zinc-900 p-2 rounded">
                      <p className="text-[10px] text-zinc-500 uppercase">High 24h</p>
                      <p className="text-sm font-bold text-emerald-400">${priceData.high_24h}</p>
                    </div>
                  )}
                  {priceData?.low_24h && (
                    <div className="bg-zinc-900 p-2 rounded">
                      <p className="text-[10px] text-zinc-500 uppercase">Low 24h</p>
                      <p className="text-sm font-bold text-red-400">${priceData.low_24h}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Market Sentiment */}
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  Market Sentiment
                </h4>
                <div className={`flex items-center gap-3 p-3 rounded-lg ${sentiment.bgColor} border ${sentiment.borderColor}`}>
                  {sentiment.color === 'emerald' || sentiment.color === 'green' ? (
                    <TrendingUp className={`w-6 h-6 text-${sentiment.color}-500`} />
                  ) : (
                    <TrendingDown className={`w-6 h-6 text-${sentiment.color}-500`} />
                  )}
                  <div>
                    <p className={`font-bold text-${sentiment.color}-400`}>{sentiment.text}</p>
                    <p className="text-[10px] text-zinc-500">Based on price movement</p>
                  </div>
                </div>
              </div>
              
              {/* Volume */}
              {priceData?.volume_formatted && (
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-500" />
                    Volume
                  </h4>
                  <p className="text-2xl font-bold text-white">{priceData.volume_formatted}</p>
                  <p className="text-xs text-zinc-500">24h Trading Volume</p>
                </div>
              )}
              
              {/* Multi-Timeframe Changes */}
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-purple-500" />
                  Performance
                </h4>
                <div className="space-y-2">
                  {[
                    { label: '1 Day', value: priceData?.change_1d },
                    { label: '1 Week', value: priceData?.change_1w },
                    { label: '1 Month', value: priceData?.change_1m },
                    { label: '1 Year', value: priceData?.change_1y }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-zinc-900 px-3 py-2 rounded">
                      <span className="text-xs text-zinc-400">{item.label}</span>
                      <span className={`text-sm font-bold ${(item.value || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {(item.value || 0) >= 0 ? '+' : ''}{(item.value || 0).toFixed(2)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Disclaimer */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-[10px] text-amber-500/80 flex items-start gap-1">
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  Data for informational purposes only. Not financial advice.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function MarketsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPro = user?.subscription_tier === "pro" && user?.is_subscribed;
  
  const [indices, setIndices] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [crypto, setCrypto] = useState([]);
  const [forex, setForex] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [viewMode, setViewMode] = useState('compact');
  const [searchFilter, setSearchFilter] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [favorites, setFavorites] = useState([]);
  const [marketStatus, setMarketStatus] = useState(null);
  const [gainersLosers, setGainersLosers] = useState({ gainers: [], losers: [] });
  const [sortBy, setSortBy] = useState('name'); // 'name', 'change', 'price'
  const [autoRefreshSec, setAutoRefreshSec] = useState(60); // 60-second cycle
  const AUTO_REFRESH_INTERVAL = 60; // seconds
  
  // NEW: ETFs and International
  const [etfs, setEtfs] = useState([]);
  const [commodityEtfs, setCommodityEtfs] = useState([]);
  const [international, setInternational] = useState([]);
  
  // POWER SEARCH State
  const [powerSearchQuery, setPowerSearchQuery] = useState('');
  const [powerSearchResult, setPowerSearchResult] = useState(null);
  const [powerSearchLoading, setPowerSearchLoading] = useState(false);
  const [showPowerSearch, setShowPowerSearch] = useState(false);
  
  // FREE VIEW BLOCKER State
  const [showFreeBlocker, setShowFreeBlocker] = useState(false);
  const [freeViewStartTime, setFreeViewStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  // CHART POPUP State
  const [chartPopup, setChartPopup] = useState({ isOpen: false, symbol: null, name: null, price: null });
  
  // MINI CHARTS Data
  const [miniCharts, setMiniCharts] = useState({});
  
  // SECTOR MODAL State
  const [sectorModal, setSectorModal] = useState({ isOpen: false, sector: null, items: [] });

  // Check if free view time has expired (1 hour limit for non-logged-in users)
  useEffect(() => {
    if (!user) {
      // Get or set the free view start time
      const storedStartTime = localStorage.getItem('freeViewStartTime');
      if (storedStartTime) {
        setFreeViewStartTime(parseInt(storedStartTime));
      } else {
        const now = Date.now();
        localStorage.setItem('freeViewStartTime', now.toString());
        setFreeViewStartTime(now);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user && freeViewStartTime) {
      const checkTime = () => {
        const elapsed = Date.now() - freeViewStartTime;
        const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
        const remaining = oneHour - elapsed;
        
        if (remaining <= 0) {
          setShowFreeBlocker(true);
          setTimeRemaining(0);
        } else {
          setTimeRemaining(Math.floor(remaining / 1000 / 60)); // minutes remaining
        }
      };
      
      checkTime();
      const interval = setInterval(checkTime, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [user, freeViewStartTime]);

  // Reset free view timer (allows another hour)
  const resetFreeViewTimer = () => {
    const now = Date.now();
    localStorage.setItem('freeViewStartTime', now.toString());
    setFreeViewStartTime(now);
    setShowFreeBlocker(false);
  };

  const authHeaders = { headers: { Authorization: `Bearer ${getToken()}` }};

  const isPositive = (value) => {
    if (!value) return false;
    return !value.toString().startsWith('-');
  };
  
  // POWER SEARCH - Search any symbol worldwide
  const handlePowerSearch = async () => {
    if (!powerSearchQuery.trim()) return;
    
    setPowerSearchLoading(true);
    setPowerSearchResult(null);
    
    try {
      const res = await axios.get(`${API}/power-search/${powerSearchQuery.trim()}`);
      setPowerSearchResult(res.data);
    } catch (error) {
      setPowerSearchResult({
        success: false,
        message: error.response?.data?.detail || "Search failed. Try again."
      });
    } finally {
      setPowerSearchLoading(false);
    }
  };
  
  // Add to favorites from power search
  const addToFavoritesFromSearch = async (symbol) => {
    if (!getToken()) {
      toast.error("Login to save favorites");
      return;
    }
    
    try {
      const res = await axios.post(`${API}/favorites/${symbol}`, {}, authHeaders);
      if (res.data.success) {
        setFavorites(prev => [...prev, symbol]);
        toast.success(`${symbol} added to favorites! (${res.data.count}/15)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add favorite");
    }
  };

  const fetchAllPrices = async () => {
    setRefreshing(true);
    try {
      const [indicesRes, stocksRes, commoditiesRes, cryptoRes, forexRes, etfsRes, commodityEtfsRes, internationalRes] = await Promise.all([
        axios.get(`${API}/indices`).catch(() => null),
        axios.get(`${API}/stocks`).catch(() => null),
        axios.get(`${API}/commodities`).catch(() => null),
        axios.get(`${API}/crypto`).catch(() => null),
        axios.get(`${API}/forex`).catch(() => null),
        axios.get(`${API}/etfs`).catch(() => null),
        axios.get(`${API}/commodity-etfs`).catch(() => null),
        axios.get(`${API}/international`).catch(() => null)
      ]);

      const indicesData = indicesRes?.data?.indices || [];
      const stocksData = stocksRes?.data?.stocks || [];
      const commoditiesData = commoditiesRes?.data?.commodities || [];
      const cryptoData = cryptoRes?.data?.crypto || [];
      const forexData = forexRes?.data?.forex || [];
      const etfsData = etfsRes?.data?.etfs || [];
      const commodityEtfsData = commodityEtfsRes?.data?.commodity_etfs || [];
      const internationalData = internationalRes?.data?.international || [];

      setIndices(indicesData);
      setStocks(stocksData);
      setCommodities(commoditiesData);
      setCrypto(cryptoData);
      setForex(forexData);
      setEtfs(etfsData);
      setCommodityEtfs(commodityEtfsData);
      setInternational(internationalData);
      
      setLastUpdated(new Date().toLocaleTimeString());
      
      // Fetch mini charts for all symbols
      const allSymbols = [
        ...indicesData.map(i => i.symbol),
        ...stocksData.map(s => s.symbol),
        ...commoditiesData.map(c => c.symbol),
        ...cryptoData.map(c => c.symbol),
        ...forexData.map(f => f.symbol),
        ...etfsData.map(e => e.symbol),
        ...commodityEtfsData.map(c => c.symbol),
        ...internationalData.map(i => i.symbol)
      ].filter(Boolean);
      
      if (allSymbols.length > 0) {
        fetchMiniCharts(allSymbols);
      }
      
    } catch (error) {
      console.error("Error fetching prices:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Fetch mini sparkline charts for all symbols
  const fetchMiniCharts = async (symbols) => {
    try {
      // Batch in chunks of 30 to avoid overloading
      const chunks = [];
      for (let i = 0; i < symbols.length; i += 30) {
        chunks.push(symbols.slice(i, i + 30));
      }
      
      for (const chunk of chunks) {
        const res = await axios.post(`${API}/charts/batch-mini`, chunk);
        if (res.data.charts) {
          setMiniCharts(prev => ({ ...prev, ...res.data.charts }));
        }
      }
    } catch (error) {
      console.debug("Mini charts not loaded:", error);
    }
  };
  
  // Open chart popup
  const openChartPopup = (symbol, name, price) => {
    setChartPopup({ isOpen: true, symbol, name, price });
  };
  
  // Close chart popup
  const closeChartPopup = () => {
    setChartPopup({ isOpen: false, symbol: null, name: null, price: null });
  };
  
  // Sector definitions with stock symbols - Extended for modal view
  const SECTOR_STOCKS = {
    Technology: ['AAPL', 'MSFT', 'GOOGL', 'NVDA', 'META', 'AMD', 'INTC', 'CRM', 'ORCL', 'ADBE', 'CSCO', 'IBM', 'QCOM', 'TXN', 'AVGO', 'NOW', 'UBER', 'PLTR', 'SNOW', 'DDOG', 'NET', 'MDB', 'PATH', 'ZM', 'ROKU'],
    Finance: ['JPM', 'BAC', 'WFC', 'GS', 'MS', 'C', 'V', 'MA', 'PYPL', 'SQ', 'BLK', 'SCHW', 'AXP', 'USB', 'PNC', 'BRK-B', 'COIN', 'HOOD', 'SOFI', 'AFRM', 'UPST', 'NU'],
    Healthcare: ['UNH', 'JNJ', 'PFE', 'ABBV', 'MRK', 'GILD', 'AMGN', 'REGN', 'VRTX', 'BIIB'],
    Consumer: ['WMT', 'COST', 'HD', 'TGT', 'LOW', 'NKE', 'SBUX', 'MCD', 'PG', 'KO', 'PEP', 'PM', 'MO', 'MDLZ', 'CL', 'ABNB', 'DASH'],
    Energy: ['XOM', 'CVX', 'COP', 'SLB', 'EOG', 'BA', 'CAT', 'GE', 'RTX', 'LMT'],
    Entertainment: ['NFLX', 'DIS', 'CMCSA', 'T', 'VZ', 'TSLA', 'DKNG', 'RBLX', 'TTWO', 'EA'],
    Semiconductor: ['MU', 'LRCX', 'KLAC', 'AMAT', 'MRVL'],
    REIT: ['AMT', 'PLD', 'CCI', 'EQIX', 'SPG'],
    Crypto: ['BTC', 'ETH', 'USDT', 'XRP', 'BNB', 'SOL', 'ADA', 'DOGE', 'DOT', 'MATIC', 'AVAX', 'LINK', 'SHIB', 'LTC', 'TRX'],
    EV: ['RIVN', 'LCID', 'NIO', 'XPEV', 'PLUG', 'ENPH'],
    Cybersecurity: ['CRWD', 'ZS', 'PANW', 'FTNT'],
  };
  
  // Open sector modal with filtered items
  const openSectorModal = (sector) => {
    const sectorSymbols = SECTOR_STOCKS[sector] || [];
    let sectorItems = [];
    
    // For crypto sector
    if (sector === 'Crypto') {
      sectorItems = crypto.filter(item => 
        sectorSymbols.some(sym => {
          const itemSym = item.symbol?.toUpperCase().replace('-USD', '');
          return itemSym === sym.toUpperCase();
        })
      );
    } else {
      // Get items from stocks
      sectorItems = stocks.filter(item => 
        sectorSymbols.some(sym => item.symbol?.toUpperCase() === sym.toUpperCase())
      );
    }
    
    console.log(`Opening ${sector} modal with ${sectorItems.length} items`);
    setSectorModal({ isOpen: true, sector, items: sectorItems });
  };
  
  // Close sector modal
  const closeSectorModal = () => {
    setSectorModal({ isOpen: false, sector: null, items: [] });
  };
  
  // Handle Pro upgrade from footer
  const handleProUpgrade = () => {
    navigate("/auth?plan=pro");
  };

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/favorites`, authHeaders);
      setFavorites(res.data.favorites || []);
    } catch (error) {
      console.log("Favorites not loaded");
    }
  };

  const fetchMarketStatus = async () => {
    try {
      const res = await axios.get(`${API}/market-status`);
      setMarketStatus(res.data);
    } catch (error) {
      console.log("Market status not loaded");
    }
  };

  const fetchGainersLosers = async () => {
    try {
      const res = await axios.get(`${API}/gainers-losers`);
      setGainersLosers(res.data);
    } catch (error) {
      console.log("Gainers/losers not loaded");
    }
  };

  const toggleFavorite = async (symbol) => {
    if (!getToken()) {
      toast.error("Login to save favorites");
      return;
    }
    
    try {
      if (favorites.includes(symbol)) {
        await axios.delete(`${API}/favorites/${symbol}`, authHeaders);
        setFavorites(favorites.filter(f => f !== symbol));
        toast.success(`${symbol} removed from favorites`);
      } else {
        await axios.post(`${API}/favorites/${symbol}`, {}, authHeaders);
        setFavorites([...favorites, symbol]);
        toast.success(`${symbol} added to favorites`);
      }
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  useEffect(() => {
    fetchAllPrices();
    fetchFavorites();
    fetchMarketStatus();
    fetchGainersLosers();
    // Countdown-based auto-refresh every 60s
    const tick = setInterval(() => {
      setAutoRefreshSec(prev => {
        if (prev <= 1) {
          fetchAllPrices();
          return AUTO_REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only: starts countdown and periodic refresh

  // Filter and sort items
  const filterItems = (items) => {
    let filtered = items;
    if (searchFilter) {
      filtered = items.filter(item => 
        item.symbol?.toLowerCase().includes(searchFilter.toLowerCase()) ||
        item.name?.toLowerCase().includes(searchFilter.toLowerCase())
      );
    }
    
    // Sort
    if (sortBy === 'change') {
      filtered = [...filtered].sort((a, b) => {
        const aChange = parseFloat(a.change_percent?.replace('%', '').replace('+', '') || 0);
        const bChange = parseFloat(b.change_percent?.replace('%', '').replace('+', '') || 0);
        return bChange - aChange;
      });
    } else if (sortBy === 'price') {
      filtered = [...filtered].sort((a, b) => {
        const aPrice = parseFloat(a.price?.replace(',', '') || 0);
        const bPrice = parseFloat(b.price?.replace(',', '') || 0);
        return bPrice - aPrice;
      });
    }
    
    return filtered;
  };

  // Get favorites data
  const favoriteItems = [...indices, ...stocks, ...commodities, ...crypto, ...forex]
    .filter(item => favorites.includes(item.symbol));

  // FAST Price Card - With mini sparkline chart and click for details
  const CompactPriceCard = ({ item, colorClass = "amber" }) => {
    const isUp = isPositive(item.change_percent);
    const isFavorite = favorites.includes(item.symbol);
    const sparklineData = miniCharts[item.symbol] || [];
    
    return (
      <div 
        className={`bg-[#0a0a0a] border-l-4 ${isUp ? 'border-l-emerald-500' : 'border-l-red-500'} border border-zinc-800/50 p-2.5 transition-all duration-150 cursor-pointer group relative hover:bg-zinc-900/70 hover:border-zinc-700`}
        onClick={() => openChartPopup(item.symbol, item.name, item.price)}
        data-testid={`price-card-${item.symbol}`}
      >
        {/* Favorites Star - Always Visible */}
        <button 
          onClick={(e) => { e.stopPropagation(); toggleFavorite(item.symbol); }}
          className={`absolute top-1 right-1 p-1 rounded transition-all z-10 ${
            isFavorite 
              ? 'text-amber-400 bg-amber-500/20' 
              : 'text-zinc-600 hover:text-amber-400 hover:bg-zinc-800/50'
          }`}
        >
          <Star className={`w-3.5 h-3.5 ${isFavorite ? 'fill-amber-400' : ''}`} />
        </button>
        
        <div className="pr-7">
          {/* Symbol + Arrow */}
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className={`text-sm font-bold text-${colorClass}-500`}>{item.symbol}</span>
            {isUp ? (
              <ArrowUp className="w-3 h-3 text-emerald-500" />
            ) : (
              <ArrowDown className="w-3 h-3 text-red-500" />
            )}
          </div>
          
          {/* Name */}
          <p className="text-xs text-zinc-600 truncate mb-1">{item.name}</p>
          
          {/* Price + Mini Chart */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-base font-bold font-data text-white">${item.price}</span>
            <MiniSparkline data={sparklineData} width={50} height={20} positive={isUp} />
          </div>
          
          {/* Change */}
          <div className="flex items-center justify-between mt-1">
            <span className={`text-sm font-bold font-data ${isUp ? 'text-emerald-500' : 'text-red-500'}`}>
              {item.change_percent}
            </span>
            <LineChart className="w-3 h-3 text-zinc-600 group-hover:text-amber-500 transition-colors" />
          </div>
        </div>
      </div>
    );
  };

  // Category counts
  const counts = {
    all: indices.length + stocks.length + commodities.length + crypto.length + forex.length + etfs.length + commodityEtfs.length + international.length,
    favorites: favoriteItems.length,
    indices: indices.length,
    stocks: stocks.length,
    etfs: etfs.length + commodityEtfs.length,
    commodities: commodities.length + commodityEtfs.length,
    crypto: crypto.length,
    forex: forex.length,
    international: international.length
  };

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate(user ? "/dashboard" : "/")}
                className="text-zinc-400 hover:text-amber-500 h-8 w-8"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div 
                className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate(user ? "/dashboard" : "/")}
                data-testid="logo-home-link"
              >
                <img src={LOGO_URL} alt="ECOLIONS" className="h-7 w-7" />
                <h1 className="text-base font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                  <span className="text-emerald-500">MARKET</span> PRICES
                </h1>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Market Status Indicators */}
              {marketStatus && (
                <div className="hidden md:flex items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded ${marketStatus.us_market.status === 'open' ? 'bg-emerald-500/20 text-emerald-500' : 'bg-zinc-800 text-zinc-500'}`}>
                    US {marketStatus.us_market.status}
                  </span>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-500">
                    Crypto 24/7
                  </span>
                </div>
              )}
              
              {/* Search */}
              <div className="relative hidden sm:block">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="pl-7 pr-3 py-1.5 bg-zinc-900 border border-zinc-800 text-xs w-32 focus:outline-none focus:border-emerald-500"
                />
              </div>
              
              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 text-xs px-2 py-1.5 text-zinc-400"
              >
                <option value="name">A-Z</option>
                <option value="change">% Change</option>
                <option value="price">Price</option>
              </select>
              
              {/* View Toggle */}
              <div className="flex items-center border border-zinc-800">
                <button
                  onClick={() => setViewMode('compact')}
                  className={`p-1.5 ${viewMode === 'compact' ? 'bg-emerald-500/20 text-emerald-500' : 'text-zinc-500'}`}
                >
                  <Grid3X3 className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setViewMode('detailed')}
                  className={`p-1.5 ${viewMode === 'detailed' ? 'bg-emerald-500/20 text-emerald-500' : 'text-zinc-500'}`}
                >
                  <List className="w-3 h-3" />
                </button>
              </div>
              
              <div className="flex items-center gap-2 text-xs" data-testid="auto-refresh-timer">
                {/* Live Countdown Bar */}
                <div className="flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 rounded-full px-3 py-1">
                  <div className="relative w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className="absolute inset-y-0 left-0 bg-emerald-500 rounded-full transition-all duration-1000 ease-linear"
                      style={{ width: `${(autoRefreshSec / AUTO_REFRESH_INTERVAL) * 100}%` }}
                    />
                  </div>
                  <span className={`font-mono font-bold tabular-nums ${autoRefreshSec <= 5 ? 'text-amber-400' : 'text-emerald-500'}`}>
                    {autoRefreshSec}s
                  </span>
                </div>
                <span className="text-zinc-600 hidden sm:inline">|</span>
                <span className="text-zinc-500 hidden sm:inline">{lastUpdated || '...'}</span>
              </div>
              
              <Button 
                onClick={() => { fetchAllPrices(); setAutoRefreshSec(AUTO_REFRESH_INTERVAL); }}
                disabled={refreshing}
                size="sm"
                className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-500 h-7 px-2"
                data-testid="manual-refresh-btn"
              >
                <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          
          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 mt-2 overflow-x-auto pb-1">
            {[
              { key: 'all', label: 'All', icon: DollarSign, color: 'emerald' },
              { key: 'favorites', label: 'Favorites', icon: Star, color: 'amber' },
              { key: 'indices', label: 'Indices', icon: LineChart, color: 'blue' },
              { key: 'stocks', label: 'US Stocks', icon: BarChart3, color: 'amber' },
              { key: 'etfs', label: 'ETFs', icon: TrendingUp, color: 'purple' },
              { key: 'international', label: 'International', icon: Globe, color: 'teal' },
              { key: 'commodities', label: 'Commodities', icon: Gem, color: 'emerald' },
              { key: 'crypto', label: 'Crypto', icon: Bitcoin, color: 'orange' },
              { key: 'forex', label: 'Forex', icon: Banknote, color: 'cyan' }
            ].map(cat => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex items-center gap-1 px-2 py-1 text-xs font-medium whitespace-nowrap transition-all ${
                  activeCategory === cat.key 
                    ? `bg-${cat.color}-500/20 text-${cat.color}-500 border border-${cat.color}-500` 
                    : 'bg-zinc-900 text-zinc-500 border border-zinc-800 hover:text-white'
                }`}
              >
                <cat.icon className="w-3 h-3" />
                {cat.label}
                <span className="opacity-60">({counts[cat.key]})</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* POWER SEARCH - Search Any Symbol Worldwide */}
      <div className="max-w-7xl mx-auto px-3 pt-3">
        <div className="bg-gradient-to-r from-amber-500/10 via-emerald-500/10 to-amber-500/10 border border-amber-500/30 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-5 h-5 text-amber-500" />
            <h2 className="text-base font-bold text-white">POWER SEARCH</h2>
            <span className="text-xs text-zinc-500">Search any stock, crypto, forex worldwide • FREE</span>
          </div>
          
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                placeholder="Search: AAPL, TSLA, BTC, GOLD, EURUSD..."
                value={powerSearchQuery}
                onChange={(e) => setPowerSearchQuery(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handlePowerSearch()}
                className="w-full pl-10 pr-4 py-2.5 bg-black border border-zinc-700 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500 rounded"
              />
            </div>
            <Button 
              onClick={handlePowerSearch}
              disabled={powerSearchLoading || !powerSearchQuery.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-6"
            >
              {powerSearchLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  SEARCH
                </>
              )}
            </Button>
          </div>
          
          {/* Search Result */}
          {powerSearchResult && (
            <div className="mt-3">
              {powerSearchResult.success && powerSearchResult.result ? (
                <div className={`bg-black border-l-4 ${isPositive(powerSearchResult.result.change_percent) ? 'border-l-emerald-500' : 'border-l-red-500'} border border-zinc-800 p-4 flex items-center justify-between`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg font-bold text-amber-500">{powerSearchResult.result.symbol}</span>
                      {isPositive(powerSearchResult.result.change_percent) ? (
                        <ArrowUp className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <ArrowDown className="w-4 h-4 text-red-500" />
                      )}
                      <span className="text-xs text-zinc-500 px-2 py-0.5 bg-zinc-800 rounded">{powerSearchResult.result.type}</span>
                    </div>
                    <p className="text-sm text-zinc-400">{powerSearchResult.result.name}</p>
                  </div>
                  
                  <div className="text-right mr-4">
                    <p className="text-2xl font-bold font-data text-white">${powerSearchResult.result.price}</p>
                    <p className={`text-lg font-bold font-data ${isPositive(powerSearchResult.result.change_percent) ? 'text-emerald-500' : 'text-red-500'}`}>
                      {powerSearchResult.result.change} ({powerSearchResult.result.change_percent})
                    </p>
                  </div>
                  
                  {/* Add to Favorites Button */}
                  <Button
                    onClick={() => addToFavoritesFromSearch(powerSearchResult.result.symbol)}
                    disabled={favorites.includes(powerSearchResult.result.symbol)}
                    className={`${
                      favorites.includes(powerSearchResult.result.symbol)
                        ? 'bg-amber-500/20 text-amber-500 cursor-default'
                        : 'bg-amber-500 hover:bg-amber-600 text-black'
                    } font-bold`}
                  >
                    {favorites.includes(powerSearchResult.result.symbol) ? (
                      <>
                        <Star className="w-4 h-4 mr-2 fill-amber-500" />
                        IN FAVORITES
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        ADD TO FAVORITES
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                  <p className="text-sm text-red-400 mb-2">{powerSearchResult.message}</p>
                  {powerSearchResult.suggestions && (
                    <div className="text-xs text-zinc-500 space-y-1">
                      <p className="text-zinc-400 font-bold">Try these formats:</p>
                      {powerSearchResult.suggestions.map((s, i) => (
                        <p key={i}>{s}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Favorites Count */}
          <div className="mt-3 flex items-center justify-between text-xs">
            <span className="text-zinc-500">
              <Star className="w-3 h-3 inline mr-1 text-amber-500" />
              Favorites: <span className="text-amber-500 font-bold">{favorites.length}/15</span>
            </span>
            <span className="text-zinc-600">Real-time prices from yfinance • Auto-refreshes every {AUTO_REFRESH_INTERVAL}s</span>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-3 py-3 space-y-4">
        {/* Show Skeleton Loading when prices are loading */}
        {loading && indices.length === 0 && (
          <div className="space-y-6">
            <SectionSkeleton title="Market Indices" count={8} />
            <SectionSkeleton title="Stocks" count={12} />
            <SectionSkeleton title="Crypto" count={8} />
            <SectionSkeleton title="Commodities" count={6} />
          </div>
        )}
        
        {/* Gainers & Losers Quick View */}
        {activeCategory === 'all' && gainersLosers.gainers.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-500/5 border border-emerald-500/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <ArrowUp className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-bold text-emerald-500 uppercase">Top Gainers</span>
              </div>
              <div className="space-y-1">
                {gainersLosers.gainers.slice(0, 3).map(item => (
                  <div key={item.symbol} className="flex justify-between text-xs">
                    <span className="text-white font-bold">{item.symbol}</span>
                    <span className="text-emerald-500 font-data">{item.change_percent}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-red-500/5 border border-red-500/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <ArrowDown className="w-4 h-4 text-red-500" />
                <span className="text-xs font-bold text-red-500 uppercase">Top Losers</span>
              </div>
              <div className="space-y-1">
                {gainersLosers.losers.slice(0, 3).map(item => (
                  <div key={item.symbol} className="flex justify-between text-xs">
                    <span className="text-white font-bold">{item.symbol}</span>
                    <span className="text-red-500 font-data">{item.change_percent}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Favorites Section */}
        {activeCategory === 'favorites' && (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-4 h-4 text-amber-500" />
              <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Your Favorites</h2>
            </div>
            {favoriteItems.length === 0 ? (
              <div className="text-center py-8 bg-zinc-900/50 border border-zinc-800">
                <Star className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">No favorites yet</p>
                <p className="text-xs text-zinc-600">Click the star on any price to add it here</p>
              </div>
            ) : (
              <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'}`}>
                {favoriteItems.map((item) => (
                  <CompactPriceCard key={item.symbol} item={item} colorClass="amber" />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Market Indices */}
        {(activeCategory === 'all' || activeCategory === 'indices') && filterItems(indices).length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <LineChart className="w-4 h-4 text-blue-500" />
              <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Market Indices</h2>
              <span className="text-xs text-zinc-600">({filterItems(indices).length})</span>
            </div>
            <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
              {filterItems(indices).map((item) => (
                <CompactPriceCard key={item.symbol} item={item} colorClass="blue" />
              ))}
            </div>
          </section>
        )}

        {/* Stocks - Organized by Sector */}
        {(activeCategory === 'all' || activeCategory === 'stocks') && filterItems(stocks).length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-amber-500" />
              <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">US Stocks by Sector</h2>
              <span className="text-xs text-zinc-600">({filterItems(stocks).length} stocks)</span>
            </div>
            
            {/* Sector Category Buttons - Click to expand */}
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { key: 'Technology', label: 'Technology', icon: Cpu, color: 'blue' },
                { key: 'Finance', label: 'Finance', icon: Landmark, color: 'emerald' },
                { key: 'Healthcare', label: 'Healthcare', icon: Heart, color: 'pink' },
                { key: 'Consumer', label: 'Consumer', icon: ShoppingCart, color: 'orange' },
                { key: 'Energy', label: 'Energy', icon: Fuel, color: 'yellow' },
                { key: 'Entertainment', label: 'Media', icon: Tv, color: 'purple' },
                { key: 'Semiconductor', label: 'Chips', icon: Cpu, color: 'cyan' },
                { key: 'EV', label: 'EV/Clean', icon: Zap, color: 'green' },
                { key: 'Cybersecurity', label: 'Security', icon: Shield, color: 'red' },
              ].map(sector => {
                const IconComp = sector.icon;
                return (
                  <button
                    key={sector.key}
                    onClick={() => openSectorModal(sector.key)}
                    data-testid={`sector-btn-${sector.key.toLowerCase()}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-${sector.color}-500/50 hover:bg-${sector.color}-500/10 transition-all group`}
                  >
                    <IconComp className={`w-3.5 h-3.5 text-${sector.color}-500`} />
                    <span className="text-xs font-bold text-white">{sector.label}</span>
                    <ChevronRight className={`w-3 h-3 text-zinc-600 group-hover:text-${sector.color}-500 transition-colors`} />
                  </button>
                );
              })}
            </div>
            
            {/* Quick Sector Filter */}
            <div className="flex flex-wrap gap-1 mb-3">
              {['All', 'Tech', 'Finance', 'Healthcare', 'Consumer', 'Energy', 'Industrial'].map(sector => (
                <button
                  key={sector}
                  onClick={() => setSearchFilter(sector === 'All' ? '' : sector.toLowerCase())}
                  className={`text-xs px-2 py-1 rounded transition-colors ${
                    (sector === 'All' && !searchFilter) || searchFilter.toLowerCase() === sector.toLowerCase()
                      ? 'bg-amber-500 text-black font-bold'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {sector}
                </button>
              ))}
            </div>
            
            {/* Tech Stocks */}
            {(() => {
              const techStocks = filterItems(stocks).filter(s => 
                ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA', 'AMD', 'INTC', 'CRM', 'ORCL', 'ADBE', 'CSCO', 'IBM', 'QCOM', 'TXN', 'AVGO', 'NOW', 'UBER', 'INTU', 'ANET', 'SNPS', 'CDNS', 'WDAY', 'TEAM'].includes(s.symbol)
              );
              return techStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="text-xs font-bold text-blue-400 uppercase">Tech Giants</span>
                    <span className="text-xs text-zinc-600">({techStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {techStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="blue" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Finance & Banking */}
            {(() => {
              const financeStocks = filterItems(stocks).filter(s => 
                ['BRK-B', 'JPM', 'BAC', 'WFC', 'GS', 'MS', 'C', 'V', 'MA', 'PYPL', 'SQ', 'BLK', 'SCHW', 'AXP', 'USB', 'PNC', 'TFC', 'COF', 'SPGI', 'MCO'].includes(s.symbol)
              );
              return financeStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    <span className="text-xs font-bold text-emerald-400 uppercase">Finance & Banking</span>
                    <span className="text-xs text-zinc-600">({financeStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {financeStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="emerald" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Healthcare & Biotech */}
            {(() => {
              const healthStocks = filterItems(stocks).filter(s => 
                ['UNH', 'JNJ', 'PFE', 'ABBV', 'MRK', 'LLY', 'TMO', 'DHR', 'BMY', 'CVS', 'GILD', 'AMGN', 'REGN', 'VRTX', 'BIIB', 'ISRG', 'MDT', 'SYK'].includes(s.symbol)
              );
              return healthStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                    <span className="text-xs font-bold text-pink-400 uppercase">Healthcare & Pharma</span>
                    <span className="text-xs text-zinc-600">({healthStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {healthStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="pink" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Consumer & Retail */}
            {(() => {
              const consumerStocks = filterItems(stocks).filter(s => 
                ['WMT', 'COST', 'HD', 'TGT', 'LOW', 'NKE', 'SBUX', 'MCD', 'PG', 'KO', 'PEP', 'PM', 'MO', 'MDLZ', 'CL', 'KMB', 'EL', 'KHC'].includes(s.symbol)
              );
              return consumerStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                    <span className="text-xs font-bold text-orange-400 uppercase">Consumer & Retail</span>
                    <span className="text-xs text-zinc-600">({consumerStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {consumerStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="orange" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Energy & Industrial */}
            {(() => {
              const energyStocks = filterItems(stocks).filter(s => 
                ['XOM', 'CVX', 'COP', 'SLB', 'EOG', 'OXY', 'MPC', 'PSX', 'VLO', 'PXD', 'HES', 'DVN', 'HAL', 'BKR', 'FANG'].includes(s.symbol)
              );
              return energyStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <span className="text-xs font-bold text-red-400 uppercase">Energy</span>
                    <span className="text-xs text-zinc-600">({energyStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {energyStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="red" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Industrial & Aerospace */}
            {(() => {
              const industrialStocks = filterItems(stocks).filter(s => 
                ['BA', 'CAT', 'GE', 'RTX', 'LMT', 'HON', 'UNP', 'DE', 'MMM', 'EMR', 'NOC', 'GD', 'ITW', 'ETN', 'WM'].includes(s.symbol)
              );
              return industrialStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <span className="text-xs font-bold text-yellow-400 uppercase">Industrial & Aerospace</span>
                    <span className="text-xs text-zinc-600">({industrialStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {industrialStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="yellow" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Semiconductors */}
            {(() => {
              const semiStocks = filterItems(stocks).filter(s => 
                ['MU', 'LRCX', 'KLAC', 'AMAT', 'MRVL', 'ADI', 'ON', 'NXPI', 'SWKS', 'MPWR', 'WOLF', 'ARM'].includes(s.symbol)
              );
              return semiStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
                    <span className="text-xs font-bold text-cyan-400 uppercase">Semiconductors</span>
                    <span className="text-xs text-zinc-600">({semiStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {semiStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="cyan" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Entertainment & Media */}
            {(() => {
              const mediaStocks = filterItems(stocks).filter(s => 
                ['NFLX', 'DIS', 'CMCSA', 'T', 'VZ', 'TMUS', 'CHTR', 'SPOT', 'WBD', 'PARA', 'FOXA', 'LYV'].includes(s.symbol)
              );
              return mediaStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                    <span className="text-xs font-bold text-purple-400 uppercase">Entertainment & Media</span>
                    <span className="text-xs text-zinc-600">({mediaStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {mediaStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="purple" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* REITs & Real Estate */}
            {(() => {
              const reitStocks = filterItems(stocks).filter(s => 
                ['AMT', 'PLD', 'CCI', 'EQIX', 'SPG', 'O', 'WELL', 'DLR', 'PSA', 'AVB'].includes(s.symbol)
              );
              return reitStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                    <span className="text-xs font-bold text-teal-400 uppercase">REITs & Real Estate</span>
                    <span className="text-xs text-zinc-600">({reitStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {reitStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="teal" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Transportation & Logistics */}
            {(() => {
              const transStocks = filterItems(stocks).filter(s => 
                ['UPS', 'FDX', 'DAL', 'UAL', 'LUV', 'AAL', 'CSX', 'NSC', 'JBHT', 'EXPD'].includes(s.symbol)
              );
              return transStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                    <span className="text-xs font-bold text-indigo-400 uppercase">Transportation</span>
                    <span className="text-xs text-zinc-600">({transStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {transStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="indigo" />
                    ))}
                  </div>
                </div>
              );
            })()}
          </section>
        )}

        {/* Commodities - Organized by Category */}
        {(activeCategory === 'all' || activeCategory === 'commodities') && filterItems(commodities).length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Gem className="w-4 h-4 text-emerald-500" />
              <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Commodities</h2>
              <span className="text-xs text-zinc-600">({filterItems(commodities).length})</span>
            </div>
            
            {/* Precious Metals */}
            {(() => {
              const metals = filterItems(commodities).filter(c => 
                ['GC', 'SI', 'PL', 'PA', 'HG', 'ALI'].some(m => c.symbol?.includes(m))
              );
              return metals.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <span className="text-xs font-bold text-yellow-400 uppercase">Precious Metals</span>
                    <span className="text-xs text-zinc-600">({metals.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {metals.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="yellow" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Energy */}
            {(() => {
              const energy = filterItems(commodities).filter(c => 
                ['CL', 'BZ', 'NG', 'RB', 'HO', 'URA'].some(e => c.symbol?.includes(e))
              );
              return energy.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <span className="text-xs font-bold text-red-400 uppercase">Energy</span>
                    <span className="text-xs text-zinc-600">({energy.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {energy.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="red" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Agriculture - Grains */}
            {(() => {
              const grains = filterItems(commodities).filter(c => 
                ['ZW', 'ZC', 'ZS', 'ZR', 'ZO'].some(g => c.symbol?.includes(g))
              );
              return grains.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                    <span className="text-xs font-bold text-amber-400 uppercase">Grains</span>
                    <span className="text-xs text-zinc-600">({grains.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {grains.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="amber" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Softs */}
            {(() => {
              const softs = filterItems(commodities).filter(c => 
                ['KC', 'CT', 'SB', 'CC', 'OJ'].some(s => c.symbol?.includes(s))
              );
              return softs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                    <span className="text-xs font-bold text-orange-400 uppercase">Softs (Coffee, Sugar, Cotton)</span>
                    <span className="text-xs text-zinc-600">({softs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {softs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="orange" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Livestock & Other */}
            {(() => {
              const livestock = filterItems(commodities).filter(c => 
                ['LE', 'HE', 'GF', 'LBS', 'DX'].some(l => c.symbol?.includes(l))
              );
              return livestock.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                    <span className="text-xs font-bold text-pink-400 uppercase">Livestock & Other</span>
                    <span className="text-xs text-zinc-600">({livestock.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {livestock.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="pink" />
                    ))}
                  </div>
                </div>
              );
            })()}
          </section>
        )}

        {/* Cryptocurrency */}
        {(activeCategory === 'all' || activeCategory === 'crypto') && filterItems(crypto).length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Bitcoin className="w-4 h-4 text-purple-500" />
              <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Cryptocurrency</h2>
              <span className="text-xs text-zinc-600">({filterItems(crypto).length})</span>
            </div>
            <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
              {filterItems(crypto).map((item) => (
                <CompactPriceCard key={item.symbol} item={item} colorClass="purple" />
              ))}
            </div>
          </section>
        )}

        {/* Forex */}
        {(activeCategory === 'all' || activeCategory === 'forex') && filterItems(forex).length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Banknote className="w-4 h-4 text-cyan-500" />
              <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Forex</h2>
              <span className="text-xs text-zinc-600">({filterItems(forex).length})</span>
            </div>
            <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
              {filterItems(forex).map((item) => (
                <CompactPriceCard key={item.symbol} item={item} colorClass="cyan" />
              ))}
            </div>
          </section>
        )}

        {/* ETFs - Index, Sector, Thematic, Bond */}
        {(activeCategory === 'all' || activeCategory === 'etfs') && (filterItems(etfs).length > 0 || filterItems(commodityEtfs).length > 0) && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-purple-500" />
              <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">ETFs</h2>
              <span className="text-xs text-zinc-600">({filterItems(etfs).length + filterItems(commodityEtfs).length})</span>
            </div>
            
            {/* Index ETFs */}
            {(() => {
              const indexEtfs = filterItems(etfs).filter(e => 
                ['SPY', 'QQQ', 'IWM', 'DIA', 'VTI', 'VOO', 'IVV', 'RSP', 'MDY', 'IJH', 'IJR', 'SCHA'].includes(e.symbol)
              );
              return indexEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="text-xs font-bold text-blue-400 uppercase">Index ETFs</span>
                    <span className="text-xs text-zinc-600">({indexEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {indexEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="blue" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* International ETFs */}
            {(() => {
              const intlEtfs = filterItems(etfs).filter(e => 
                ['VEA', 'VWO', 'EFA', 'EEM', 'IEFA', 'IEMG', 'VXUS', 'FXI', 'EWJ', 'EWG'].includes(e.symbol)
              );
              return intlEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                    <span className="text-xs font-bold text-teal-400 uppercase">International ETFs</span>
                    <span className="text-xs text-zinc-600">({intlEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {intlEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="teal" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Sector ETFs */}
            {(() => {
              const sectorEtfs = filterItems(etfs).filter(e => 
                ['XLK', 'XLF', 'XLE', 'XLV', 'XLI', 'XLY', 'XLP', 'XLU', 'XLRE', 'XLC', 'XLB', 'VNQ'].includes(e.symbol)
              );
              return sectorEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    <span className="text-xs font-bold text-emerald-400 uppercase">Sector ETFs</span>
                    <span className="text-xs text-zinc-600">({sectorEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {sectorEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="emerald" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Tech & Software ETFs - IGV and more! */}
            {(() => {
              const techEtfs = filterItems(etfs).filter(e => 
                ['IGV', 'SOXX', 'SMH', 'FTEC', 'VGT', 'WCLD', 'CLOU', 'CIBR'].includes(e.symbol)
              );
              return techEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                    <span className="text-xs font-bold text-indigo-400 uppercase">Tech & Software ETFs</span>
                    <span className="text-xs text-zinc-600">({techEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {techEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="indigo" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Thematic & ARK ETFs */}
            {(() => {
              const thematicEtfs = filterItems(etfs).filter(e => 
                ['ARKK', 'ARKW', 'ARKG', 'ARKF', 'ARKQ', 'BOTZ', 'ROBO', 'AIQ'].includes(e.symbol)
              );
              return thematicEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-pink-500"></div>
                    <span className="text-xs font-bold text-pink-400 uppercase">Growth & Innovation ETFs</span>
                    <span className="text-xs text-zinc-600">({thematicEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {thematicEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="pink" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Dividend ETFs */}
            {(() => {
              const dividendEtfs = filterItems(etfs).filter(e => 
                ['VYM', 'SCHD', 'DVY', 'HDV', 'NOBL', 'SPHD'].includes(e.symbol)
              );
              return dividendEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-xs font-bold text-green-400 uppercase">Dividend ETFs</span>
                    <span className="text-xs text-zinc-600">({dividendEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {dividendEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="green" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Bond ETFs */}
            {(() => {
              const bondEtfs = filterItems(etfs).filter(e => 
                ['TLT', 'IEF', 'SHY', 'BND', 'AGG', 'LQD', 'HYG', 'VCIT'].includes(e.symbol)
              );
              return bondEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
                    <span className="text-xs font-bold text-cyan-400 uppercase">Bond ETFs</span>
                    <span className="text-xs text-zinc-600">({bondEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {bondEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="cyan" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Commodity ETFs - Precious Metals */}
            {(() => {
              const metalEtfs = filterItems(commodityEtfs).filter(e => 
                ['GLD', 'IAU', 'SLV', 'PPLT', 'PALL', 'GLTR', 'CPER', 'DBB', 'JJN', 'COPX', 'REMX'].includes(e.symbol)
              );
              return metalEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <span className="text-xs font-bold text-yellow-400 uppercase">Metals ETFs</span>
                    <span className="text-xs text-zinc-600">({metalEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {metalEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="yellow" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Energy ETFs */}
            {(() => {
              const energyEtfs = filterItems(commodityEtfs).filter(e => 
                ['USO', 'UNG', 'USL', 'BNO', 'IEZ', 'XOP'].includes(e.symbol)
              );
              return energyEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <span className="text-xs font-bold text-red-400 uppercase">Energy ETFs</span>
                    <span className="text-xs text-zinc-600">({energyEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {energyEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="red" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Agriculture ETFs */}
            {(() => {
              const agriEtfs = filterItems(commodityEtfs).filter(e => 
                ['DBA', 'MOO', 'WEAT', 'CORN', 'SOYB'].includes(e.symbol)
              );
              return agriEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-lime-500"></div>
                    <span className="text-xs font-bold text-lime-400 uppercase">Agriculture ETFs</span>
                    <span className="text-xs text-zinc-600">({agriEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {agriEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="lime" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Crypto ETFs */}
            {(() => {
              const cryptoEtfs = filterItems(commodityEtfs).filter(e => 
                ['IBIT', 'FBTC', 'GBTC', 'ETHE', 'BITO', 'ARKB'].includes(e.symbol)
              );
              return cryptoEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                    <span className="text-xs font-bold text-orange-400 uppercase">Crypto ETFs</span>
                    <span className="text-xs text-zinc-600">({cryptoEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {cryptoEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="orange" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Clean Energy & Uranium ETFs */}
            {(() => {
              const cleanEtfs = filterItems(commodityEtfs).filter(e => 
                ['URA', 'URNM', 'ICLN', 'TAN', 'LIT'].includes(e.symbol)
              );
              return cleanEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    <span className="text-xs font-bold text-emerald-400 uppercase">Clean Energy & Uranium ETFs</span>
                    <span className="text-xs text-zinc-600">({cleanEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {cleanEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="emerald" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Broad Commodity ETFs */}
            {(() => {
              const broadEtfs = filterItems(commodityEtfs).filter(e => 
                ['DBC', 'GSG', 'PDBC', 'COM'].includes(e.symbol)
              );
              return broadEtfs.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                    <span className="text-xs font-bold text-amber-400 uppercase">Broad Commodity ETFs</span>
                    <span className="text-xs text-zinc-600">({broadEtfs.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {broadEtfs.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="amber" />
                    ))}
                  </div>
                </div>
              );
            })()}
          </section>
        )}

        {/* International Stocks */}
        {(activeCategory === 'all' || activeCategory === 'international') && filterItems(international).length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-4 h-4 text-teal-500" />
              <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">International Stocks</h2>
              <span className="text-xs text-zinc-600">({filterItems(international).length})</span>
            </div>
            
            {/* European */}
            {(() => {
              const euroStocks = filterItems(international).filter(s => 
                ['NESN.SW', 'MC.PA', 'SAP', 'ASML', 'SHEL', 'AZN', 'NVO'].includes(s.symbol)
              );
              return euroStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <span className="text-xs font-bold text-blue-400 uppercase">European</span>
                    <span className="text-xs text-zinc-600">({euroStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {euroStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="blue" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Asian */}
            {(() => {
              const asianStocks = filterItems(international).filter(s => 
                ['TM', 'SONY', 'TSM', 'BABA', 'JD', 'PDD', 'BIDU', 'NIO', 'SE', 'GRAB'].includes(s.symbol)
              );
              return asianStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <span className="text-xs font-bold text-red-400 uppercase">Asian Markets</span>
                    <span className="text-xs text-zinc-600">({asianStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {asianStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="red" />
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Latin America */}
            {(() => {
              const latamStocks = filterItems(international).filter(s => 
                ['MELI', 'NU', 'VALE'].includes(s.symbol)
              );
              return latamStocks.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-xs font-bold text-green-400 uppercase">Latin America</span>
                    <span className="text-xs text-zinc-600">({latamStocks.length})</span>
                  </div>
                  <div className={`grid gap-2 ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'}`}>
                    {latamStocks.map((item) => (
                      <CompactPriceCard key={item.symbol} item={item} colorClass="green" />
                    ))}
                  </div>
                </div>
              );
            })()}
          </section>
        )}

        {/* Pro CTA */}
        {!isPro && (
          <section className="bg-gradient-to-r from-amber-500/10 to-emerald-500/10 border border-amber-500/30 p-4 text-center">
            <Crown className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Barlow Condensed' }}>
              UNLOCK AI MARKET INSIGHTS
            </h3>
            <p className="text-xs text-zinc-400 mb-3">
              Get AI predictions, trending alerts, and personalized analysis.
            </p>
            <Button 
              onClick={() => navigate("/auth")}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-4 py-1.5 text-sm"
            >
              Upgrade to Pro - $49.97/mo
            </Button>
          </section>
        )}
        
        {/* Footer Disclaimer with DOSLIONS branding and Pro Upgrade */}
        <FooterDisclaimer onUpgrade={handleProUpgrade} />
      </main>

      {/* FREE VIEW BLOCKER - Shows after 1 hour for non-logged-in users */}
      {showFreeBlocker && !user && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-lg">
          <div className="bg-[#111111] border-2 border-amber-500/50 w-full max-w-md shadow-2xl relative animate-in fade-in zoom-in duration-200">
            {/* Warning Icon */}
            <div className="p-8 text-center border-b border-zinc-800">
              <div className="w-20 h-20 bg-amber-500/20 mx-auto flex items-center justify-center mb-4 rounded-full animate-pulse">
                <Clock className="w-10 h-10 text-amber-500" />
              </div>
              <h3 className="text-2xl font-bold text-white uppercase tracking-wider mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                Free View Time Expired
              </h3>
              <p className="text-sm text-zinc-400">
                Your 1-hour free market access has ended
              </p>
            </div>
            
            {/* Options */}
            <div className="p-6 space-y-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider font-bold text-center mb-4">
                Choose to continue
              </p>
              
              {/* Option 1: Become Pro */}
              <Button 
                onClick={() => {
                  setShowFreeBlocker(false);
                  navigate("/auth?plan=pro");
                }}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider py-5 text-base"
                data-testid="markets-blocker-pro-btn"
              >
                <Crown className="w-5 h-5 mr-2" />
                Become Pro Ecopreneur - $49.97
              </Button>
              
              {/* Option 2: Create Free Account */}
              <Button 
                onClick={() => {
                  setShowFreeBlocker(false);
                  navigate("/auth");
                }}
                variant="outline"
                className="w-full border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10 font-bold uppercase tracking-wider py-5"
                data-testid="markets-blocker-account-btn"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Create Free Account
              </Button>
              
              {/* Option 3: Refresh for another hour */}
              <button 
                onClick={resetFreeViewTimer}
                className="w-full text-center text-xs text-zinc-600 hover:text-zinc-400 py-2 underline transition-colors"
                data-testid="markets-blocker-refresh-btn"
              >
                Continue with another 1-hour free view
              </button>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-zinc-800 bg-zinc-900/50">
              <p className="text-xs text-zinc-500 text-center">
                <AlertTriangle className="w-3 h-3 inline mr-1 text-amber-500" />
                Free users don't have dashboard access. Create an account to unlock more features.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Chart Popup Modal */}
      <ChartPopup 
        symbol={chartPopup.symbol}
        name={chartPopup.name}
        currentPrice={chartPopup.price}
        isOpen={chartPopup.isOpen}
        onClose={closeChartPopup}
      />
      
      {/* Sector Modal - Expanded view of sector stocks */}
      <SectorModal
        isOpen={sectorModal.isOpen}
        onClose={closeSectorModal}
        sector={sectorModal.sector}
        items={sectorModal.items}
        onItemClick={openChartPopup}
        miniCharts={miniCharts}
      />
    </div>
  );
}
